/**
 * useChatPresence — tab-visibility axis of the `chat.chattingWith` Redux
 * presence field, plus `claim` / `release` helpers the main hook calls from
 * its `useBasePage` route-visibility lifecycle.
 *
 * Extracted from `useChat.ts` to keep the main hook below the size threshold
 * (`presentation.md`).
 *
 * Two distinct presence axes exist:
 *  - **Route axis** (Flutter `didPopNext` / `didPushNext` / `dispose`,
 *    `chat_screen.dart:157-160, 184-202`): handled in the main hook via
 *    `useBasePage` — the main hook owns the lifecycle so it can also bind
 *    initial-load and cleanup side-effects there. It calls `claim()` /
 *    `release()` from this hook to perform the actual dispatches.
 *  - **Tab axis** (Flutter `AppLifecycleState.resumed` / `.paused` in
 *    `didChangeAppLifecycleState`, `chat_screen.dart:131-153`): owned here
 *    via `useTabVisibility`.
 */
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import type { AppDispatch } from '@/presentation/store';
import { setChattingWith } from '@/presentation/store/slices/auth/authSlice';
import { useTabVisibility } from '@/presentation/utils/tabVisibility';

export interface UseChatPresenceResult {
  /** Dispatch `setChattingWith(peerId)` — call when entering chat focus. */
  claim: () => void;
  /** Dispatch `setChattingWith('')` — call when leaving chat focus. */
  release: () => void;
}

export const useChatPresence = (
  peerId: string,
  /**
   * Optional observer notified on every tab-visibility transition (visible ⇔
   * Flutter `AppLifecycleState.resumed` / `index == 0`; hidden ⇔ `.paused` /
   * `index == 2`). Invoked from the SAME `useTabVisibility` subscription that
   * drives presence, so there is exactly ONE chat-presence visibility listener.
   * `useChat` passes this to keep its `isTabVisibleRef` (the web mirror of
   * Flutter `_appLifecycleState`) fresh for the live-listener background gate —
   * see `useChatLiveListener.handleAdded` / `chat_screen.dart:543-579` — AND to
   * re-arm the unread pill on the visible transition when peer messages arrived
   * while hidden (Flutter's resume branch, `chat_screen.dart:141-152`).
   */
  onVisibilityChange?: (isVisible: boolean) => void,
): UseChatPresenceResult => {
  const dispatch = useDispatch<AppDispatch>();

  const claim = useCallback(() => {
    dispatch(setChattingWith(peerId));
  }, [dispatch, peerId]);

  const release = useCallback(() => {
    dispatch(setChattingWith(''));
  }, [dispatch]);

  // Tab axis (Flutter `AppLifecycleState.resumed` / `.paused`).
  useTabVisibility((isVisible) => {
    // Mirror the visibility flag out first so any consumer ref is fresh before
    // the presence dispatch (and before the next live-listener `added` reads
    // it). `useTabVisibility` captures this closure in a ref, so reading the
    // latest `onVisibilityChange` each call is safe.
    onVisibilityChange?.(isVisible);
    if (isVisible) {
      claim();
    } else {
      release();
    }
  });

  return { claim, release };
};
